<script setup lang="ts">
import { AdSlot, AppShell } from '@it-tools/shell'
import { useI18n } from 'vue-i18n'
import HomeFaq from './components/HomeFaq.vue'
import ToolCard from './components/ToolCard.vue'
import ValueProps from './components/ValueProps.vue'
import { TOOLS } from './model/tools'

/** Three short reasons to trust the page, under the headline. Keep them true. */
const POINTS = ['free', 'private', 'shareable'] as const

/**
 * The hero's illustration: a /24 split once, then one half split again, which is
 * what the subnet calculator does. CIDR notation is never translated.
 */
const SPLIT = [
  [{ cidr: '10.0.0.0/24', span: 4, depth: 0 }],
  [
    { cidr: '10.0.0.0/25', span: 2, depth: 1 },
    { cidr: '10.0.0.128/25', span: 2, depth: 1 },
  ],
  [
    { cidr: '10.0.0.0/26', span: 1, depth: 2 },
    { cidr: '10.0.0.64/26', span: 1, depth: 2 },
    { cidr: '10.0.0.128/25', span: 2, depth: 1 },
  ],
] as const

const { t } = useI18n()
</script>

<template>
  <!-- No tool name: on this page the site itself is the subject, so the hero
       below carries the <h1> and the shell shows the logo alone. -->
  <AppShell>
    <div class="layout">
      <div class="layout__main">
        <section class="hero" aria-labelledby="hero-heading">
          <div class="hero__text">
            <h1 id="hero-heading" class="hero__title">{{ t('home.heading') }}</h1>
            <p class="hero__lead">{{ t('home.lead') }}</p>
            <ul class="hero__points">
              <li v-for="key in POINTS" :key="key">{{ t(`home.hero.points.${key}`) }}</li>
            </ul>
          </div>

          <div class="hero__art" aria-hidden="true">
            <div v-for="(row, index) in SPLIT" :key="index" class="split">
              <span
                v-for="block in row"
                :key="block.cidr"
                class="split__block mono"
                :data-depth="block.depth"
                :style="{ gridColumn: `span ${block.span}` }"
              >
                {{ block.cidr }}
              </span>
            </div>
          </div>
        </section>

        <section class="section" aria-labelledby="tools-heading">
          <h2 id="tools-heading">{{ t('home.tools.heading') }}</h2>
          <ul class="tools">
            <li v-for="tool in TOOLS" :key="tool.id">
              <ToolCard :tool="tool" />
            </li>
          </ul>
        </section>

        <AdSlot placement="leaderboard" />

        <ValueProps class="section" />
        <HomeFaq class="section" />
      </div>

      <AdSlot placement="sidebar" />
    </div>
  </AppShell>
</template>

<style scoped>
.layout {
  display: flex;
  gap: var(--space-6);
  align-items: flex-start;
}

.layout__main {
  flex: 1;
  min-width: 0;
}

/* ---------- hero ---------- */

.hero {
  position: relative;
  display: grid;
  gap: var(--space-8);
  align-items: center;
  padding: clamp(var(--space-6), 5vw, 3.5rem);
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  isolation: isolate;
}

/* A faint drafting grid: a workbench, not a billboard. */
.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  background-image:
    linear-gradient(var(--color-border) 1px, transparent 1px),
    linear-gradient(90deg, var(--color-border) 1px, transparent 1px);
  background-size: 24px 24px;
  opacity: 0.35;
}

@media (min-width: 960px) {
  .hero {
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  }
}

.hero__title {
  max-width: 20ch;
  margin: 0;
  font-size: clamp(1.875rem, 4vw, 2.75rem);
  line-height: 1.1;
  letter-spacing: -0.02em;
}

.hero__lead {
  max-width: 46rem;
  margin: var(--space-4) 0 0;
  font-size: var(--text-lg);
  color: var(--color-text-muted);
}

.hero__points {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin: var(--space-6) 0 0;
  padding: 0;
  list-style: none;
}

.hero__points li {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: var(--color-surface-raised);
  font-size: var(--text-sm);
  font-weight: 500;
}

.hero__points li::before {
  content: '';
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: var(--color-success);
}

/* The illustration only earns its space beside the text, not above it. */
.hero__art {
  display: none;
}

@media (min-width: 960px) {
  .hero__art {
    display: grid;
    gap: var(--space-2);
    padding: var(--space-4);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    background: var(--color-bg);
    box-shadow: var(--shadow-md);
  }
}

.split {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-2);
}

.split__block {
  overflow: hidden;
  padding: var(--space-3) var(--space-2);
  border: 1px solid var(--color-accent);
  border-radius: var(--radius-sm);
  background: var(--color-accent-subtle);
  font-size: 0.75rem;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.split__block[data-depth='0'] {
  border-color: var(--color-border-strong);
  background: var(--color-surface);
}

.split__block[data-depth='2'] {
  border-color: var(--color-split-bg);
  background: var(--color-surface);
  color: var(--color-split-ink);
}

/* ---------- sections ---------- */

.section {
  margin-top: clamp(var(--space-8), 6vw, 4rem);
}

h2 {
  margin: 0 0 var(--space-4);
  font-size: 1.35rem;
}

.tools {
  display: grid;
  gap: var(--space-4);
  grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
  margin: 0;
  padding: 0;
  list-style: none;
}
</style>
